/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { Models } from "@Internal";

import { Coupon, CouponUsage } from "App/Models";
import { Coupons, CouponsStop, CouponsUsage } from "App/IOC/Services";

import Configuration from "../Configuration";
import Columns from "./Columns";
import Fields from "./Fields";
import Filters from "./Filters";
import { AsyncHandleError, Dictionary } from "@Internal/Contracts";
import { CustomError } from "@Internal/Models";
import { ProcessException } from "App/Externals/Services/Base";

/* Stop a coupon now: blocks future redemptions and ends every active subscription discount. The
   backend returns nothing on success, so the caller refetches the coupon to reflect the change. */
async function Stop( id: number ): Promise<CustomError | undefined> {
    try {
        const results = await CouponsStop.update( id, {} );

        if( results !== undefined && results.Error !== undefined )
            return new CustomError( results.Error.Id, results.Error.Message );

    } catch( error ) {
        return ProcessException( error );
    }

    return undefined;
}

/* Customers who redeemed this coupon + the date of their first subscription using it. The backend
   returns one record carrying the list under "Usages"; each row is deserialized so its
   FirstSubscriptionDate becomes a usable ICustomDate. */
async function Usage( id: number ): Promise<[ CouponUsage[], CustomError | undefined ]> {
    try {
        const result = await CouponsUsage.get( id );

        if( result === undefined || result.Usages === undefined )
            return [ [], undefined ];

        const usages: CouponUsage[] = [];

        for( const raw of result.Usages ) {
            const [ usage, error ] = Models.Deserialize<CouponUsage>( raw );

            if( error !== undefined || usage === undefined )
                return [ [], error ];

            usages.push( usage );
        }

        return [ usages, undefined ];

    } catch( error ) {
        return [ [], await ProcessException( error ) ];
    }
}

async function GenerateGeneralConditions( coupon: Coupon ): AsyncHandleError<string> {
    let conditions: string = "COUPON_discount";
    const values: Dictionary = {
        percentage: coupon.Percentage,
        fixedAmount: coupon.FixedAmount,
        duration: coupon.Duration
    };

    if( coupon.Percentage === undefined && coupon.FixedAmount === undefined )
        return [ $t( conditions + "No" ), undefined ];

    /* Get the yearly coupon defined in global configuration. */
    const [ configuration, error ] = await Configuration.Get();

    if(
        error !== undefined ||
        configuration === undefined ||
        configuration.YearlyCoupon === undefined
    )
        return [ undefined, error ];

    /* Since this coupon could be used as a yearly coupon, so in that case we don't
       need to show duration in months. */
    if( coupon.Id === configuration.YearlyCoupon.Id ) {
        conditions += "Yearly";
        delete values.duration;
    }

    conditions += coupon.Percentage !== undefined ? "Percentage" : "FixedAmount";

    return [ $t( conditions, values ), undefined ];
}

export default {
    Columns,
    Delete: Coupons.Delete,
    Create: Coupons.Create,
    Fields,
    Filters,
    Find: Coupons.Find,
    Get: Coupons.Get,
    GenerateGeneralConditions,
    Stop,
    Update: Coupons.Update,
    Usage
};
